import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Caja, type Ganancia } from "@/lib/api";
import { cajaPorDefecto, hayVariasCajas, opcionesCaja } from "@/lib/cajas";
import { aPayloadReparto, formReparto, validarReparto, type ErroresReparto, type FormReparto } from "@/lib/ganancia";

const SIN_ERRORES: ErroresReparto = {};
type Dialogo = "repartir" | "borrar" | null;

/** Lógica de Ganancia (sin JSX): reparto único, al final, de la plata de ventas entre los clase A. Sólo el admin reparte o lo deshace. */
export function useGananciaTab() {
  const { fid, membresia } = useContextoApp();
  const url = `/api/fideicomisos/${fid}/ganancia`;
  const esAdmin = membresia.rol === "admin";
  const ganancia = useApiResource<Ganancia>(url);
  const cajas = useApiResource<Caja[]>(esAdmin ? `/api/fideicomisos/${fid}/cajas` : null);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormReparto>(() => formReparto("0"));
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionRepartir = useAsyncAction((datos: ReturnType<typeof aPayloadReparto>) => api.post<Ganancia>(url, datos));
  const accionBorrar = useAsyncAction(() => api.delete<void>(url).then(() => true));

  const errores = validarReparto(form);
  const cerrar = () => setDialogo(null);
  const g = ganancia.data ?? null;

  return {
    esAdmin,
    cargando: !g && ganancia.loading,
    errorCarga: g ? null : (ganancia.error?.message ?? null),
    reintentar: ganancia.recargar,
    ganancia: g,
    repartida: g?.reparto != null,
    dialogo,
    cerrar,
    abrirRepartir: () => {
      setForm(formReparto(g?.disponible_ventas_usd ?? "0", undefined, cajaPorDefecto(cajas.data ?? [])));
      setMostrarErrores(false);
      accionRepartir.limpiarError();
      setDialogo("repartir");
    },
    abrirBorrar: () => {
      accionBorrar.limpiarError();
      setDialogo("borrar");
    },
    form,
    // De qué caja sale el reparto (Mati, 2026-10-19); con una sola caja activa no se pregunta: va a la principal.
    opcionesCaja: hayVariasCajas(cajas.data ?? []) ? opcionesCaja(cajas.data ?? []) : null,
    cambiar: <K extends keyof FormReparto>(campo: K, valor: FormReparto[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    repartir: async (e: FormEvent) => {
      e.preventDefault();
      setMostrarErrores(true);
      if (Object.keys(errores).length > 0) return;
      if (await accionRepartir.run(aPayloadReparto(form))) {
        cerrar();
        ganancia.recargar();
      }
    },
    repartiendo: accionRepartir.loading,
    errorRepartir: accionRepartir.error,
    confirmarBorrar: async () => {
      if (await accionBorrar.run()) {
        cerrar();
        ganancia.recargar();
      }
    },
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
